import { Check, ChevronRight, ChevronsUpDown, FileDown, FileSpreadsheet, Filter, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SearchableSelectDialog } from './SearchableSelectDialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PureDate } from '@/lib/absolute-datetime'
import { calculateTotalHours, formatHours, normalizeText } from '@/lib/timesheet-utils'
import { type TimesheetLineToAddDTO, TimesheetsService, type TimesheetToAddDTO } from '@/services/timesheets'
import type { Company } from '@/types/company'
import type { HourType } from '@/types/hour-type'
import type { Project } from '@/types/project'
import type { Timesheet } from '@/types/timesheet'
import type { User } from '@/types/user'

interface TimesheetFilters {
	from?: string
	to?: string
	date?: string
	userID?: string
}

interface CompanyTimesheetsTabProps {
	timesheets: Timesheet[]
	users: User[]
	loading: boolean
	error: string | null
	filters: TimesheetFilters
	onFiltersChange: (filters: TimesheetFilters) => void
	onTimesheetSelect: (timesheet: Timesheet) => void
	company: Company
	projects: Project[]
	hourTypes: HourType[]
	onTimesheetCreated?: (timesheet: Timesheet) => void
}

export function CompanyTimesheetsTab({
	timesheets,
	users,
	loading,
	error,
	filters,
	onFiltersChange,
	onTimesheetSelect,
	company,
	projects,
	hourTypes,
	onTimesheetCreated,
}: CompanyTimesheetsTabProps) {
	const [userFilterOpen, setUserFilterOpen] = useState(false)
	const [filtersDialogOpen, setFiltersDialogOpen] = useState(false)
	const [createDialogOpen, setCreateDialogOpen] = useState(false)
	const [createUserOpen, setCreateUserOpen] = useState(false)
	const [creating, setCreating] = useState(false)
	const [exporting, setExporting] = useState(false)

	// Estados del formulario de creación
	const [formDate, setFormDate] = useState(() => {
		const today = new PureDate()
		return today.getDate()
	})
	const [formUserIDs, setFormUserIDs] = useState<string[]>([])

	// Estado para las líneas temporales
	interface TempLine {
		id: string
		projectID: string
		hourTypeID: string
		start: string
		end: string
		text: string
	}
	const [tempLines, setTempLines] = useState<TempLine[]>([])
	const [searchSelect, setSearchSelect] = useState<{ lineId: string; type: 'project' | 'hourType' } | null>(null)

	const handleClearFilters = () => {
		onFiltersChange({
			from: undefined,
			to: undefined,
			date: undefined,
			userID: undefined,
		})
	}

	const handleExportExcel = async () => {
		try {
			setExporting(true)
			const from = filters.from ?? filters.date
			const to = filters.to ?? filters.date
			const { blob, filename } = await TimesheetsService.exportTimesheetsExcel({
				from,
				to,
				userID: filters.userID,
			})
			const url = window.URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = filename
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			window.URL.revokeObjectURL(url)
		} catch (err) {
			console.error('Error al exportar registros:', err)
			toast.error(err instanceof Error ? err.message : 'Error al exportar registros horarios')
		} finally {
			setExporting(false)
		}
	}

	const handleDownload = async (timesheet: Timesheet, e: React.MouseEvent) => {
		e.stopPropagation()
		try {
			const blob = await TimesheetsService.downloadTimesheet(timesheet.id)
			const url = window.URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = `${timesheet.user.name}_${timesheet.date.replace(/-/g, '')}.pdf`
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			window.URL.revokeObjectURL(url)
		} catch (err) {
			console.error('Error al descargar PDF:', err)
			toast.error('Error al descargar el PDF')
		}
	}

	const selectedUser = users.find((u) => u.id === filters.userID)
	const activeFiltersCount = ['from', 'to', 'date', 'userID'].reduce(
		(acc, key) => (filters[key as keyof typeof filters] ? acc + 1 : acc),
		0
	)
	const hasActiveFilters = activeFiltersCount > 0

	const handleAddLine = () => {
		const newLine: TempLine = {
			id: `temp-${Date.now()}-${Math.random()}`,
			projectID: '',
			hourTypeID: '',
			start: '09:00',
			end: '17:00',
			text: '',
		}
		setTempLines([...tempLines, newLine])
	}

	const handleRemoveLine = (lineId: string) => {
		setTempLines(tempLines.filter((line) => line.id !== lineId))
	}

	const handleUpdateLine = (lineId: string, updates: Partial<TempLine>) => {
		setTempLines(tempLines.map((line) => (line.id === lineId ? { ...line, ...updates } : line)))
	}

	const handleCreateTimesheet = async () => {
		if (formUserIDs.length === 0 || !formDate) {
			toast.error('Por favor selecciona la fecha y al menos un empleado')
			return
		}

		if (tempLines.length === 0) {
			toast.error('Debes agregar al menos una línea')
			return
		}

		// Validar todas las líneas y construir TimesheetLineDTO[]
		const dateObj = new PureDate(formDate)
		const timesheetLines: TimesheetLineToAddDTO[] = []

		for (const line of tempLines) {
			if (!line.projectID || !line.hourTypeID || !line.start || !line.end) {
				toast.error('Todas las líneas deben tener proyecto, tipo de hora, inicio y fin completos')
				return
			}

			const [startHoursStr, startMinutesStr] = line.start.split(':')
			const [endHoursStr, endMinutesStr] = line.end.split(':')

			if (!startHoursStr || !startMinutesStr || !endHoursStr || !endMinutesStr) {
				toast.error('Formato de hora inválido en alguna línea')
				return
			}

			const startHours = Number(startHoursStr)
			const startMinutes = Number(startMinutesStr)
			const endHours = Number(endHoursStr)
			const endMinutes = Number(endMinutesStr)

			const startDate = dateObj.setTime(startHours, startMinutes)
			const endDate = dateObj.setTime(endHours, endMinutes)

			// Validar que la hora de inicio sea anterior a la hora de fin
			const comparison = startDate.compare(endDate)
			if (comparison >= 0) {
				toast.error('La hora de inicio debe ser anterior a la hora de fin en todas las líneas')
				return
			}

			// Construir TimesheetLineDTO
			const lineDTO: TimesheetLineToAddDTO = {
				end: endDate.toString(),
				hourTypeID: line.hourTypeID,
				projectID: line.projectID,
				start: startDate.toString(),
				text: line.text.trim() || '',
			}

			timesheetLines.push(lineDTO)
		}

		try {
			setCreating(true)

			// Crear un timesheet por cada usuario seleccionado
			const createdTimesheets: Timesheet[] = []
			let successCount = 0
			let errorCount = 0

			for (const userID of formUserIDs) {
				try {
					const timesheetId = crypto.randomUUID()
					const timesheetDTO: TimesheetToAddDTO = {
						id: timesheetId,
						company: company.id,
						user: userID,
						date: formDate,
						file: null,
						lines: timesheetLines,
					}

					const newTimesheet = await TimesheetsService.createTimesheet(timesheetDTO)
					createdTimesheets.push(newTimesheet)
					successCount++
				} catch (err) {
					console.error(`Error al crear timesheet para usuario ${userID}:`, err)
					errorCount++
				}
			}

			if (successCount > 0) {
				if (formUserIDs.length === 1) {
					toast.success('Registro horario creado correctamente')
				} else {
					toast.success(
						`Se crearon ${successCount} registro${successCount > 1 ? 's' : ''} horario${successCount > 1 ? 's' : ''} correctamente${errorCount > 0 ? ` (${errorCount} error${errorCount > 1 ? 'es' : ''})` : ''}`
					)
				}
			}

			if (errorCount > 0 && successCount === 0) {
				toast.error('Error al crear los registros horarios')
			}

			setCreateDialogOpen(false)

			// Resetear el formulario
			setFormDate(() => {
				const today = new PureDate()
				return today.getDate()
			})
			setFormUserIDs([])
			setTempLines([])

			// Notificar sobre los timesheets creados
			if (onTimesheetCreated && createdTimesheets.length > 0) {
				// Si solo se creó uno, pasar ese, si no, refrescar la lista
				if (createdTimesheets.length === 1) {
					onTimesheetCreated(createdTimesheets[0]!)
				} else {
					// Si hay múltiples, simplemente refrescar
					onTimesheetCreated(createdTimesheets[0]!)
				}
			}
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al crear los registros horarios')
		} finally {
			setCreating(false)
		}
	}

	return (
		<Card>
			<CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<div className="space-y-1">
					<CardTitle>Registros horarios</CardTitle>
					{hasActiveFilters && (
						<p className="text-sm text-muted-foreground">
							{activeFiltersCount === 1 ? '1 filtro activo' : `${activeFiltersCount} filtros activos`}
						</p>
					)}
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={handleClearFilters}
						disabled={!hasActiveFilters}
						className="cursor-pointer"
					>
						Limpiar filtros
					</Button>
					<Button
						variant={hasActiveFilters ? 'default' : 'outline'}
						size="sm"
						onClick={() => setFiltersDialogOpen(true)}
						className="gap-2 cursor-pointer"
					>
						<Filter className="h-4 w-4" />
						Filtros
						{hasActiveFilters && (
							<span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground text-xs font-semibold text-primary">
								{activeFiltersCount}
							</span>
						)}
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={handleExportExcel}
						disabled={exporting}
						className="gap-2 cursor-pointer"
					>
						<FileSpreadsheet className="h-4 w-4" />
						{exporting ? 'Exportando...' : 'Exportar Excel'}
					</Button>
					<Button
						variant="default"
						size="sm"
						onClick={() => setCreateDialogOpen(true)}
						className="gap-2 cursor-pointer"
					>
						<Plus className="h-4 w-4" />
						Crear registro
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<Dialog open={filtersDialogOpen} onOpenChange={setFiltersDialogOpen}>
					<DialogContent className="sm:max-w-[600px]">
						<DialogHeader>
							<DialogTitle>Filtros</DialogTitle>
							<DialogDescription>
								Filtra los registros horarios por fecha o empleado.{' '}
								{hasActiveFilters &&
									`(${activeFiltersCount} ${activeFiltersCount === 1 ? 'filtro activo' : 'filtros activos'})`}
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="grid gap-4 md:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="timesheet-date">Fecha específica</Label>
									<Input
										id="timesheet-date"
										type="date"
										value={filters.date || ''}
										onChange={(e) =>
											onFiltersChange({
												...filters,
												date: e.target.value || undefined,
												from: undefined,
												to: undefined,
											})
										}
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="timesheet-user">Empleado</Label>
									<Popover open={userFilterOpen} onOpenChange={setUserFilterOpen}>
										<PopoverTrigger asChild>
											<Button
												variant="outline"
												role="combobox"
												aria-expanded={userFilterOpen}
												className="w-full justify-between"
											>
												{selectedUser ? `${selectedUser.name}` : 'Todos los empleados'}
												<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-[280px] p-0">
											<Command
												filter={(value, search) => {
													const normalizedValue = normalizeText(value)
													const normalizedSearch = normalizeText(search)
													return normalizedValue.includes(normalizedSearch) ? 1 : 0
												}}
											>
												<CommandInput placeholder="Buscar empleado..." className="h-9" />
												<CommandList>
													<CommandEmpty>No se encontraron resultados.</CommandEmpty>
													<CommandGroup>
														<CommandItem
															value="__all__"
															onSelect={() => {
																onFiltersChange({
																	...filters,
																	userID: undefined,
																})
																setUserFilterOpen(false)
															}}
														>
															<Check
																className={`mr-2 h-4 w-4 ${
																	!filters.userID ? 'opacity-100' : 'opacity-0'
																}`}
															/>
															Todos los empleados
														</CommandItem>
														{users.map((u) => (
															<CommandItem
																key={u.id}
																value={u.name}
																onSelect={() => {
																	onFiltersChange({
																		...filters,
																		userID: u.id,
																	})
																	setUserFilterOpen(false)
																}}
															>
																<Check
																	className={`mr-2 h-4 w-4 ${
																		filters.userID === u.id
																			? 'opacity-100'
																			: 'opacity-0'
																	}`}
																/>
																{u.name}
															</CommandItem>
														))}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="timesheet-from">Desde</Label>
									<Input
										id="timesheet-from"
										type="date"
										value={filters.from || ''}
										max={filters.to || undefined}
										onChange={(e) => {
											const value = e.target.value || undefined
											if (value && filters.to && value > filters.to) {
												return
											}
											onFiltersChange({
												...filters,
												from: value,
												date: undefined,
											})
										}}
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="timesheet-to">Hasta</Label>
									<Input
										id="timesheet-to"
										type="date"
										value={filters.to || ''}
										min={filters.from || undefined}
										onChange={(e) => {
											const value = e.target.value || undefined
											if (value && filters.from && value < filters.from) {
												return
											}
											onFiltersChange({
												...filters,
												to: value,
												date: undefined,
											})
										}}
									/>
								</div>
							</div>
						</div>
						<DialogFooter>
							<Button variant="ghost" onClick={handleClearFilters} disabled={!hasActiveFilters}>
								Limpiar filtros
							</Button>
							<Button onClick={() => setFiltersDialogOpen(false)}>Aplicar filtros</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Dialog para crear nuevo registro horario */}
				<Dialog
					open={createDialogOpen}
					onOpenChange={(open) => {
						setCreateDialogOpen(open)
						if (!open) {
							// Resetear al cerrar
							setFormDate(() => {
								const today = new PureDate()
								return today.getDate()
							})
							setFormUserIDs([])
							setTempLines([])
						}
					}}
				>
					<DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col">
						<DialogHeader>
							<DialogTitle>Crear registro horario</DialogTitle>
							<DialogDescription>
								Completa los datos generales y agrega las líneas de horario.
							</DialogDescription>
						</DialogHeader>
						{/* Datos generales fijos */}
						<div className="flex-shrink-0 border-b pb-4 space-y-3">
							<div className="flex items-end gap-4">
								<div className="space-y-1.5">
									<Label htmlFor="create-date">Fecha *</Label>
									<Input
										id="create-date"
										type="date"
										value={formDate}
										onChange={(e) => setFormDate(e.target.value)}
										required
										className="w-[160px]"
									/>
								</div>
								<div className="space-y-1.5 flex-1">
									<Label htmlFor="create-user">Empleados *</Label>
									<Popover open={createUserOpen} onOpenChange={setCreateUserOpen}>
										<PopoverTrigger asChild>
											<Button
												variant="outline"
												role="combobox"
												aria-expanded={createUserOpen}
												className="w-full justify-between"
											>
												{formUserIDs.length === 0
													? 'Selecciona empleados'
													: formUserIDs.length === 1
														? users.find((u) => u.id === formUserIDs[0])?.name
														: `${formUserIDs.length} empleados seleccionados`}
												<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-[280px] p-0">
											<Command
												filter={(value, search) => {
													const normalizedValue = normalizeText(value)
													const normalizedSearch = normalizeText(search)
													return normalizedValue.includes(normalizedSearch) ? 1 : 0
												}}
											>
												<CommandInput placeholder="Buscar empleado..." className="h-9" />
												<CommandList>
													<CommandEmpty>No se encontraron resultados.</CommandEmpty>
													<CommandGroup>
														{users.map((u) => {
															const isSelected = formUserIDs.includes(u.id)
															return (
																<CommandItem
																	key={u.id}
																	value={u.name}
																	onSelect={() => {
																		if (isSelected) {
																			setFormUserIDs(
																				formUserIDs.filter((id) => id !== u.id)
																			)
																		} else {
																			setFormUserIDs([...formUserIDs, u.id])
																		}
																	}}
																>
																	<Check
																		className={`mr-2 h-4 w-4 ${
																			isSelected ? 'opacity-100' : 'opacity-0'
																		}`}
																	/>
																	{u.name}
																</CommandItem>
															)
														})}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleAddLine}
									className="gap-2 flex-shrink-0"
								>
									<Plus className="h-4 w-4" />
									Agregar línea
								</Button>
							</div>
							{formUserIDs.length > 0 && (
								<div className="flex flex-wrap gap-2">
									{formUserIDs.map((userID) => {
										const user = users.find((u) => u.id === userID)
										if (!user) return null
										return (
											<Badge key={userID} variant="secondary" className="gap-1 pr-1">
												{user.name}
												<button
													type="button"
													onClick={() => {
														setFormUserIDs(formUserIDs.filter((id) => id !== userID))
													}}
													className="ml-1 rounded-full hover:bg-secondary-foreground/20"
												>
													<X className="h-3 w-3" />
												</button>
											</Badge>
										)
									})}
								</div>
							)}
						</div>

						{/* Líneas scrollables */}
						<div className="overflow-y-auto flex-1 min-h-0 py-2 space-y-2">
							{tempLines.length === 0 ? (
								<p className="text-sm text-muted-foreground text-center py-4">
									No hay líneas agregadas. Haz clic en "Agregar línea" para comenzar.
								</p>
							) : (
								tempLines.map((line, index) => (
									<div key={line.id} className="border rounded-lg p-3 space-y-2">
										<div className="flex items-center gap-2">
											<span className="text-xs text-muted-foreground font-medium w-5 flex-shrink-0">
												{index + 1}
											</span>
											<div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 flex-1 items-end">
												<Button
													type="button"
													variant="outline"
													className="h-9 justify-between font-normal truncate"
													onClick={() => setSearchSelect({ lineId: line.id, type: 'project' })}
												>
													<span className={`truncate ${line.projectID ? '' : 'text-muted-foreground'}`}>
														{line.projectID
															? (projects.find((p) => p.id === line.projectID)?.name || 'Proyecto')
															: 'Proyecto'}
													</span>
													<ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
												</Button>
												<Button
													type="button"
													variant="outline"
													className="h-9 justify-between font-normal truncate"
													onClick={() => setSearchSelect({ lineId: line.id, type: 'hourType' })}
												>
													<span className={`truncate ${line.hourTypeID ? '' : 'text-muted-foreground'}`}>
														{line.hourTypeID
															? (hourTypes.find((h) => h.id === line.hourTypeID)?.name || 'Tipo de hora')
															: 'Tipo de hora'}
													</span>
													<ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
												</Button>
												<Input
													type="time"
													value={line.start}
													onChange={(e) =>
														handleUpdateLine(line.id, { start: e.target.value })
													}
													className="h-9 w-[110px]"
													required
												/>
												<Input
													type="time"
													value={line.end}
													onChange={(e) => handleUpdateLine(line.id, { end: e.target.value })}
													className="h-9 w-[110px]"
													required
												/>
											</div>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => handleRemoveLine(line.id)}
												className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
										<div className="pl-7">
											<Input
												value={line.text}
												onChange={(e) => handleUpdateLine(line.id, { text: e.target.value })}
												placeholder="Notas (opcional)"
												className="h-8 text-sm"
											/>
										</div>
									</div>
								))
							)}
						</div>
						<DialogFooter className="flex-shrink-0">
							<Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
								Cancelar
							</Button>
							<Button
								onClick={handleCreateTimesheet}
								disabled={creating || tempLines.length === 0 || formUserIDs.length === 0}
							>
								{creating
									? formUserIDs.length > 1
										? `Creando ${formUserIDs.length} registros...`
										: 'Creando...'
									: formUserIDs.length > 1
										? `Crear ${formUserIDs.length} registros`
										: 'Crear registro'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{searchSelect && (
					<SearchableSelectDialog
						open={true}
						onOpenChange={(open) => {
							if (!open) setSearchSelect(null)
						}}
						title={searchSelect.type === 'project' ? 'Seleccionar proyecto' : 'Seleccionar tipo de hora'}
						items={searchSelect.type === 'project' ? projects : hourTypes}
						selectedId={
							searchSelect.type === 'project'
								? (tempLines.find((l) => l.id === searchSelect.lineId)?.projectID || '')
								: (tempLines.find((l) => l.id === searchSelect.lineId)?.hourTypeID || '')
						}
						onSelect={(id) => {
							handleUpdateLine(
								searchSelect.lineId,
								searchSelect.type === 'project' ? { projectID: id } : { hourTypeID: id }
							)
						}}
					/>
				)}

				{!loading && timesheets.length === 0 && !error && (
					<p className="py-8 text-center text-sm text-muted-foreground">
						No hay registros horarios para los filtros seleccionados.
					</p>
				)}

				{loading && (
					<div className="space-y-3">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</div>
				)}

				{!loading && !error && timesheets.length > 0 && (
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Empleado</TableHead>
									<TableHead>Fecha</TableHead>
									<TableHead>Líneas</TableHead>
									<TableHead>Total horas</TableHead>
									<TableHead className="w-24 text-center">Acciones</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{timesheets.map((ts) => (
									<TableRow
										key={ts.id}
										className="cursor-pointer hover:bg-muted/50"
										onClick={() => onTimesheetSelect(ts)}
									>
										<TableCell className="font-medium">{ts.user.name}</TableCell>
										<TableCell>{new PureDate(ts.date).toSpanishDate()}</TableCell>
										<TableCell>{ts.lines.length}</TableCell>
										<TableCell className="font-semibold">
											{formatHours(calculateTotalHours(ts.lines))}
										</TableCell>
										<TableCell
											className="text-center"
											onClick={(e) => {
												e.stopPropagation()
											}}
										>
											<Button variant="ghost" size="icon" onClick={(e) => handleDownload(ts, e)}>
												<FileDown className="h-4 w-4" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
